import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Settings,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Upload,
  Lightbulb,
  Wrench,
  Building2,
  Bell,
  Star,
  Phone,
  Database,
  AlertTriangle,
} from 'lucide-react';
import {
  SERVICE_CATEGORIES,
  getServiceCategoryLabel,
  UF_VALUES,
  segmentsFromSpecialties,
} from '@mustauto/types';
import {
  cleanCep, cleanCnpj, cleanPhone, formatApiError, isValidCep, isValidCnpj,
  lookupCep, maskCep, maskCnpj, maskPhone,
  STALE_OS_HOURS_CHOICES, defaultStaleOsHours, staleOsHoursLabel,
} from '@mustauto/utils';
import { ErrorState, useConfirm, useToast } from '@mustauto/ui/web';
import { EmptyState } from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@mustauto/ui/web';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatReais } from '@/lib/utils';
import {
  useLaborPrices,
  useGenerateLaborSuggestions,
  useWorkshopProfile,
  useClearDemoData,
  useWorkshopModules,
} from '@/hooks/useSettings';
import type { LaborPrice, WorkshopProfile } from '@/hooks/useSettings';
import { useEmailSettings } from '@/hooks/useEmailSettings';
import { useServiceProviders } from '@/hooks/useServiceProviders';
import type { ServiceProvider, CreateProviderData, UpdateProviderData } from '@/hooks/useServiceProviders';
import { SOFT_BADGE_CLASSES } from '@mustauto/ui/web';
import { SpecialtyPicker, cn } from '@mustauto/ui/web';
import { WorkshopModulesCard } from '@/components/WorkshopModulesCard';
import { StagesCard } from '@/components/StagesCard';
import { EstheticPackagesCard } from '@/components/EstheticPackagesCard';
import { useCan } from '@/hooks/useCan';

type Tab = 'labor' | 'profile' | 'notifications' | 'providers';

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const DEFAULT_HOURS = DAY_NAMES.map((_, i) => ({
  day: i,
  open: '08:00',
  close: '18:00',
  closed: i === 0,
}));

export function SettingsPage() {
  const can = useCan();
  // Cada aba pede a permissão do que ela muda (Fase 3); as notificações são da
  // própria pessoa e valem para todos.
  const tabs = (
    [
      { key: 'labor', label: 'Mão de Obra', icon: <Wrench size={16} />, show: can('oficina.precos') },
      { key: 'providers', label: 'Prestadores', icon: <Star size={16} />, show: can('oficina.configurar') },
      { key: 'profile', label: 'Perfil da Oficina', icon: <Building2 size={16} />, show: can('oficina.configurar') },
      { key: 'notifications', label: 'Notificações', icon: <Bell size={16} />, show: true },
    ] satisfies { key: Tab; label: string; icon: React.ReactNode; show: boolean }[]
  ).filter((t) => t.show);
  const [chosenTab, setActiveTab] = useState<Tab | null>(null);
  const activeTab: Tab = chosenTab && tabs.some((t) => t.key === chosenTab) ? chosenTab : (tabs[0]?.key ?? 'notifications');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Configurações</h1>
        <p className="text-sm text-muted-foreground">
          Gerencie preços e o perfil da oficina. A equipe e o que cada um pode fazer ficam em Equipe.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'labor' && <LaborTab />}
      {activeTab === 'providers' && <ProvidersTab />}
      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
    </div>
  );
}

// ========================================
// Aba 1 — Mão de Obra
// ========================================

function LaborTab() {
  const {
    prices,
    isError: pricesError,
    error: pricesErrorObj,
    refetch: refetchPrices,
    isLoading,
    suggestions,
    create,
    update,
    remove,
    approve,
    reject,
    isCreating,
  } = useLaborPrices();
  const { toast } = useToast();
  const confirm = useConfirm();
  const generateMutation = useGenerateLaborSuggestions();
  const generatedRef = useRef(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<LaborPrice | null>(null);

  // Pacotes de estética moram na mesma tabela (A7), mas têm cartão próprio:
  // aqui ficam só os serviços de mão de obra.
  const modulesQuery = useWorkshopModules();
  const allPrices = prices;
  const packages = allPrices.filter((p) => p.module === 'estetica');
  const services = allPrices.filter((p) => p.module !== 'estetica');
  const showPackages =
    packages.length > 0 ||
    !!modulesQuery.data?.modules.some((m) => m.module === 'estetica' && m.enabled);

  // Auto-generate suggestions on mount
  useEffect(() => {
    if (!generatedRef.current) {
      generatedRef.current = true;
      generateMutation.mutate();
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* Suggestions card */}
      <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900/60 dark:bg-amber-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-amber-800 dark:text-amber-200">
            <Lightbulb size={18} />
            Sugestões de preço pendentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {suggestions.length > 0 ? (
            <div className="space-y-3">
              {suggestions.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between rounded-lg border border-amber-200 bg-card p-3 dark:border-amber-900/60"
                >
                  <div>
                    <span className="font-medium">{s.serviceName}</span>
                    <span className="mx-2 text-muted-foreground">
                      {formatReais(s.currentPrice)} &rarr;{' '}
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                        {formatReais(s.suggestedPrice)}
                      </span>
                    </span>
                    {s.reason && (
                      <span className="text-xs text-muted-foreground">— {s.reason}</span>
                    )}
                    {s.basedOnOsCount != null && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        (baseado em {s.basedOnOsCount} OS)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => approve(s.id)}
                      className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
                    >
                      <Check size={14} /> Aplicar
                    </button>
                    <button
                      onClick={() => reject(s.id)}
                      className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X size={14} /> Ignorar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Nota dentro do card de sugestões, não estado vazio de lista:
            // o card tem título e moldura próprios.
            <p className="text-sm text-amber-700/70 dark:text-amber-300/70">
              Nenhuma sugestão no momento. O motor de aprendizado analisa os preços praticados nas OS e sugere ajustes quando detecta divergências recorrentes.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          {showAddForm ? 'Cancelar' : 'Novo Serviço'}
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <LaborPriceForm
          onSubmit={async (data) => {
            try {
              await create(data);
              setShowAddForm(false);
              toast({ type: 'success', title: 'Serviço cadastrado' });
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao cadastrar serviço',
                description: formatApiError(err, 'Verifique os campos e tente novamente.'),
              });
            }
          }}
          isSubmitting={isCreating}
        />
      )}

      {/* Edit modal */}
      {editingItem && (
        <LaborPriceEditModal
          item={editingItem}
          onSubmit={async (data) => {
            try {
              await update({ id: editingItem.id, ...data });
              setEditingItem(null);
              toast({ type: 'success', title: 'Serviço atualizado' });
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao atualizar serviço',
                description: formatApiError(err, 'Verifique os campos e tente novamente.'),
              });
            }
          }}
          onCancel={() => setEditingItem(null)}
        />
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : pricesError && allPrices.length === 0 ? (
        /* "Nenhum serviço cadastrado" com a tabela de preços cheia convida o
           gestor a recadastrar tudo. */
        <ErrorState
          title="Não foi possível carregar os preços"
          error={pricesErrorObj}
          onRetry={() => refetchPrices()}
        />
      ) : services.length === 0 ? (
        <EmptyState
          icon={<Wrench size={32} />}
          title="Nenhum serviço cadastrado"
          description="Cadastre seus preços de mão de obra para padronizar orçamentos e acelerar a montagem de OS."
          action={{
            label: 'Adicionar primeiro serviço',
            onClick: () => setShowAddForm(true),
          }}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Serviço</TableHead>
                <TableHead className="text-right">Preço base</TableHead>
                <TableHead className="text-right">Nacional</TableHead>
                <TableHead className="text-right">Importado</TableHead>
                <TableHead className="text-right">Luxo</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.serviceName}</TableCell>
                  <TableCell className="text-right">{formatReais(p.basePrice)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatReais(p.basePrice * p.modifierNational)}
                    <span className="ml-1 text-xs">({p.modifierNational}x)</span>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatReais(p.basePrice * p.modifierImported)}
                    <span className="ml-1 text-xs">({p.modifierImported}x)</span>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatReais(p.basePrice * p.modifierLuxury)}
                    <span className="ml-1 text-xs">({p.modifierLuxury}x)</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingItem(p)}
                        title="Editar"
                        aria-label="Editar serviço"
                        className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Excluir este serviço?',
                            description: 'A ação é permanente — não dá pra desfazer.',
                            confirmLabel: 'Excluir',
                            dangerous: true,
                          });
                          if (!ok) return;
                          try {
                            await remove(p.id);
                            toast({ type: 'success', title: 'Serviço removido' });
                          } catch (err) {
                            toast({
                              type: 'error',
                              title: 'Falha ao remover serviço',
                              description: formatApiError(err, 'Tente novamente em instantes.'),
                            });
                          }
                        }}
                        title="Excluir"
                        aria-label="Excluir serviço"
                        className="rounded-md border border-border p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {showPackages && !isLoading && !(pricesError && allPrices.length === 0) && (
        <EstheticPackagesCard packages={packages} />
      )}
    </div>
  );
}

function LaborPriceForm({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (data: {
    serviceName: string;
    basePrice: number;
    modifierNational?: number;
    modifierImported?: number;
    modifierLuxury?: number;
  }) => Promise<void>;
  isSubmitting: boolean;
}) {
  const [serviceName, setServiceName] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [modNational, setModNational] = useState('1.00');
  const [modImported, setModImported] = useState('1.30');
  const [modLuxury, setModLuxury] = useState('1.50');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await onSubmit({
        serviceName,
        basePrice: Number(basePrice),
        modifierNational: Number(modNational),
        modifierImported: Number(modImported),
        modifierLuxury: Number(modLuxury),
      });
    },
    [serviceName, basePrice, modNational, modImported, modLuxury, onSubmit],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold text-foreground">Novo serviço de mão de obra</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Nome do serviço *</label>
              <input
                type="text"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                required
                maxLength={200}
                placeholder="Ex: Troca de óleo"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Preço base (R$) *</label>
              <input
                type="number"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                required
                min={0}
                step="0.01"
                placeholder="0.00"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Multiplicador Nacional</label>
              <input
                type="number"
                value={modNational}
                onChange={(e) => setModNational(e.target.value)}
                min={0}
                step="0.01"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Multiplicador Importado</label>
              <input
                type="number"
                value={modImported}
                onChange={(e) => setModImported(e.target.value)}
                min={0}
                step="0.01"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Multiplicador Luxo</label>
              <input
                type="number"
                value={modLuxury}
                onChange={(e) => setModLuxury(e.target.value)}
                min={0}
                step="0.01"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting || !serviceName.trim() || !basePrice}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Salvando...' : 'Adicionar serviço'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
}

function LaborPriceEditModal({
  item,
  onSubmit,
  onCancel,
}: {
  item: LaborPrice;
  onSubmit: (data: {
    serviceName?: string;
    basePrice?: number;
    modifierNational?: number;
    modifierImported?: number;
    modifierLuxury?: number;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  useEscapeKey(onCancel);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [serviceName, setServiceName] = useState(item.serviceName);
  const [basePrice, setBasePrice] = useState(String(item.basePrice));
  const [modNational, setModNational] = useState(String(item.modifierNational));
  const [modImported, setModImported] = useState(String(item.modifierImported));
  const [modLuxury, setModLuxury] = useState(String(item.modifierLuxury));
  const [saving, setSaving] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSaving(true);
      try {
        await onSubmit({
          serviceName,
          basePrice: Number(basePrice),
          modifierNational: Number(modNational),
          modifierImported: Number(modImported),
          modifierLuxury: Number(modLuxury),
        });
      } finally {
        setSaving(false);
      }
    },
    [serviceName, basePrice, modNational, modImported, modLuxury, onSubmit],
  );

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            Editar serviço
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Nome do serviço</label>
              <input
                type="text"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                maxLength={200}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Preço base (R$)</label>
                <input
                  type="number"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  min={0}
                  step="0.01"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Nacional</label>
                <input
                  type="number"
                  value={modNational}
                  onChange={(e) => setModNational(e.target.value)}
                  min={0}
                  step="0.01"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Importado</label>
                <input
                  type="number"
                  value={modImported}
                  onChange={(e) => setModImported(e.target.value)}
                  min={0}
                  step="0.01"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Luxo</label>
                <input
                  type="number"
                  value={modLuxury}
                  onChange={(e) => setModLuxury(e.target.value)}
                  min={0}
                  step="0.01"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ========================================
// Aba 2 — Perfil da Oficina
// ========================================

function ProfileTab() {
  const { profile, isLoading, updateProfile, getPresignedUrl, isSaving } =
    useWorkshopProfile();
  const clearDemoData = useClearDemoData();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { isDemo, user } = useAuth();

  // Só a SAÍDA: ligar demonstração na oficina do cliente não existe mais (a demo
  // é a oficina compartilhada do login, sem cadastro). O card aparece enquanto
  // houver o que limpar — incluindo o seed que sobra com a flag já desligada,
  // que antes ficava preso sem nenhum controle na tela.
  //
  // Escondido na PRÓPRIA demonstração: ali a oficina é toda fictícia por
  // definição, e o visitante anônimo não tem nada a limpar. O demo-guard já
  // recusaria o POST, mas um botão destrutivo à mostra num tour de vendas é
  // ruído — ele existe para o gestor que herdou o seed na oficina de verdade.
  const hasDemoData = !isDemo && profile?.hasDemoData === true;

  const handleClearDemoData = useCallback(async () => {
    const ok = await confirm({
      title: 'Limpar dados de demonstração?',
      description:
        'Remove as OS, veículos, clientes, estoque e preços fictícios desta oficina. Seus dados reais não são afetados. Não dá para desfazer.',
      confirmLabel: 'Limpar',
      dangerous: true,
    });
    if (!ok) return;
    try {
      const res = await clearDemoData.mutateAsync();
      toast({
        type: 'success',
        title: res.cleared
          ? 'Dados de demonstração removidos'
          : 'Não havia dados de demonstração nesta oficina',
      });
    } catch (err) {
      // Sem isto o erro virava rejeição não tratada: o botão voltava ao normal e
      // a tela não dizia nada (Regra 11 — falha nunca fica silenciosa).
      toast({
        type: 'error',
        title: 'Não foi possível limpar os dados de demonstração',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  }, [clearDemoData, confirm, toast]);
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [cep, setCep] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [staleOsHours, setStaleOsHours] = useState<number>(STALE_OS_HOURS_CHOICES[0]);
  const [listedInSearch, setListedInSearch] = useState(true);
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [initialized, setInitialized] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  // Init state from loaded profile
  if (profile && !initialized) {
    const s = profile.settings;
    setName(profile.name);
    setLegalName(profile.legalName ?? '');
    setCnpj(maskCnpj(profile.cnpj ?? ''));
    setPhone(maskPhone(s.phone ?? ''));
    if (profile.address) {
      setCep(profile.address.zip ?? '');
      setStreet(profile.address.street ?? '');
      setNumber(profile.address.number ?? '');
      setComplement(profile.address.complement ?? '');
      setNeighborhood(profile.address.neighborhood ?? '');
      setCity(profile.address.city ?? '');
      setAddrState(profile.address.state ?? '');
    }
    setSpecialties(s.specialties ?? []);
    // `staleOs` pode não vir: painel e API sobem em deploys separados. O
    // fallback usa a mesma regra do servidor, então o valor é o mesmo.
    setStaleOsHours(
      profile.staleOs?.hours ??
        defaultStaleOsHours(segmentsFromSpecialties(s.specialties ?? [])),
    );
    setListedInSearch(s.listedInSearch !== false);
    if (s.hours && s.hours.length === 7) setHours(s.hours);
    setInitialized(true);
  }

  // Reage à especialidade marcada AGORA, não à que o servidor conhece: quem
  // acabou de marcar "Funilaria" vê a sugestão mudar antes de salvar, que é o
  // que explica de onde o número veio.
  const suggestedStaleOsHours = defaultStaleOsHours(segmentsFromSpecialties(specialties));

  const toggleSpecialty = useCallback((key: string) => {
    setSpecialties((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  }, []);

  const handleCepBlur = useCallback(async () => {
    if (!isValidCep(cep)) return;
    setCepLoading(true);
    const found = await lookupCep(cep);
    if (found) {
      setStreet(found.street);
      setNeighborhood(found.neighborhood);
      setCity(found.city);
      setAddrState(found.state);
    }
    setCepLoading(false);
  }, [cep]);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast({ type: 'error', title: 'Nome fantasia obrigatório' });
      return;
    }
    // CNPJ é obrigatório na oficina, mas aceitamos vazio no form pra não
    // travar cadastro legado sem CNPJ — só validamos o que foi digitado.
    const cnpjDigits = cleanCnpj(cnpj);
    if (cnpjDigits && !isValidCnpj(cnpjDigits)) {
      toast({
        type: 'error',
        title: 'CNPJ inválido',
        description: 'Confira os 14 dígitos — os dois últimos não conferem.',
      });
      return;
    }
    const phoneDigits = cleanPhone(phone);
    if (phoneDigits && phoneDigits.length < 10) {
      toast({
        type: 'error',
        title: 'Telefone incompleto',
        description: 'Informe DDD + número (10 ou 11 dígitos).',
      });
      return;
    }

    // CEP e UF são os dois campos com formato fechado na API (regex de 8
    // dígitos e enum de UF). Como agora vão sempre, um valor pela metade
    // viraria 400 cru no lugar de aviso — valida aqui. Vazio passa: é
    // remoção.
    const cleaned = cleanCep(cep);
    if (cleaned && !isValidCep(cleaned)) {
      toast({
        type: 'error',
        title: 'CEP incompleto',
        description: 'Informe os 8 dígitos ou deixe o campo vazio.',
      });
      return;
    }
    const uf = addrState.trim().toUpperCase();
    if (uf && !(UF_VALUES as readonly string[]).includes(uf)) {
      toast({
        type: 'error',
        title: 'UF inválida',
        description: 'Use a sigla de duas letras do estado (ex.: SP).',
      });
      return;
    }

    await updateProfile({
      name: trimmedName,
      legalName: legalName.trim(),
      // Vazio = remoção deliberada. Omitir a chave faria o PATCH pular a
      // coluna e o CNPJ antigo reapareceria no próximo load.
      cnpj: cnpjDigits,
      phone: phoneDigits,
      // Mesma regra para o endereço inteiro: o formulário é a verdade do que
      // está na tela, então cada campo vai como está — inclusive vazio. Com o
      // spread condicional de antes, apagar rua, número, complemento, bairro,
      // cidade ou UF não removia nada: a chave sumia do corpo, o PATCH pulava
      // a coluna e o valor antigo voltava no reload.
      addressZip: cleaned,
      addressStreet: street.trim(),
      addressNumber: number.trim(),
      addressComplement: complement.trim(),
      addressNeighborhood: neighborhood.trim(),
      addressCity: city.trim(),
      addressState: uf,
      specialties,
      // Escolher exatamente o sugerido APAGA o override em vez de gravar um
      // número igual a ele. A diferença aparece depois: a oficina que marcar
      // "Funilaria" mês que vem passa a receber 4 dias sozinha, em vez de
      // ficar presa no valor que alguém confirmou hoje sem querer mudar nada.
      //
      // O sugerido é o das especialidades DO FORMULÁRIO, não o que o servidor
      // devolveu no último load: os dois vão no mesmo PATCH, então é o do
      // formulário que vai valer assim que ele gravar.
      staleOsHours: staleOsHours === suggestedStaleOsHours ? null : staleOsHours,
      listedInSearch,
      hours,
    });
    toast({ type: 'success', title: 'Perfil salvo' });
  }, [name, legalName, cnpj, phone, cep, street, number, complement, neighborhood, city, addrState, specialties, staleOsHours, suggestedStaleOsHours, listedInSearch, hours, updateProfile, toast]);

  const handlePhotoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !profile) return;

      const currentPhotos = profile.settings.photos ?? [];
      if (currentPhotos.length >= 5) {
        toast({
          type: 'warning',
          title: 'Limite de fotos atingido',
          description: 'Você já tem 5 fotos no perfil. Remova alguma antes de adicionar outra.',
        });
        return;
      }

      setUploading(true);
      try {
        const { uploadUrl, s3Key } = await getPresignedUrl();
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': 'image/jpeg' },
        });
        await updateProfile({
          photos: [...currentPhotos.map(toPhotoKey), s3Key],
        });
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [profile, getPresignedUrl, updateProfile],
  );

  const handleRemovePhoto = useCallback(
    async (index: number) => {
      if (!profile) return;
      const photos = [...(profile.settings.photos ?? [])];
      photos.splice(index, 1);
      await updateProfile({ photos: photos.map(toPhotoKey) });
    },
    [profile, updateProfile],
  );

  const updateHour = useCallback(
    (day: number, field: 'open' | 'close' | 'closed', value: string | boolean) => {
      setHours((prev) =>
        prev.map((h) => (h.day === day ? { ...h, [field]: value } : h)),
      );
    },
    [],
  );

  if (isLoading || !profile) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Só acusa erro com os 14 dígitos digitados — enquanto o usuário digita,
  // o campo está incompleto, não inválido.
  const cnpjInvalid = cleanCnpj(cnpj).length === 14 && !isValidCnpj(cnpj);

  const photos = profile.settings.photos ?? [];
  // F-UI Bug B.1: fallback DEVE apontar pro bucket PUBLIC. Backend usa
  // generatePublicUploadUrl → S3_BUCKET_PUBLIC ('mustauto-media-public'),
  // mas o fallback antigo aqui mandava 'mustauto-media-private' → NoSuchKey
  // ao renderizar (arquivo está no public, frontend procurava no private).
  const s3Base = import.meta.env.VITE_S3_PUBLIC_URL || 'https://s3.mustauto.com.br/mustauto-media-public';
  const photoSrc = (p: string): string =>
    p.startsWith('http://') || p.startsWith('https://') ? p : `${s3Base}/${p}`;
  const toPhotoKey = (p: string): string =>
    p.match(/\/(media\/[^?]+)/)?.[1] ?? (p.startsWith('media/') ? p : p);

  return (
    <div className="space-y-6">
      {/* Dados cadastrais */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dados cadastrais</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="ws-name" className="text-xs font-medium text-muted-foreground">
                Nome fantasia
              </label>
              <input
                id="ws-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                placeholder="Oficina do Zé"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="ws-legal-name" className="text-xs font-medium text-muted-foreground">
                Razão social
              </label>
              <input
                id="ws-legal-name"
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                maxLength={255}
                placeholder="Razão social (opcional)"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="ws-cnpj" className="text-xs font-medium text-muted-foreground">
                CNPJ
              </label>
              <input
                id="ws-cnpj"
                type="text"
                inputMode="numeric"
                value={cnpj}
                onChange={(e) => setCnpj(maskCnpj(e.target.value))}
                maxLength={18}
                placeholder="00.000.000/0000-00"
                aria-invalid={cnpjInvalid}
                className={`rounded-md border bg-background px-3 py-2 font-mono text-sm ${
                  cnpjInvalid ? 'border-red-500' : 'border-input'
                }`}
              />
              {cnpjInvalid && (
                <span className="text-xs text-red-600 dark:text-red-400">
                  CNPJ inválido — confira os dígitos.
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="ws-phone" className="text-xs font-medium text-muted-foreground">
                Telefone de contato
              </label>
              <input
                id="ws-phone"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(maskPhone(e.target.value))}
                maxLength={16}
                placeholder="(11) 90000-0000"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editable fields */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Informações editáveis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">CEP</label>
              <div className="relative">
                <input
                  type="text"
                  value={cep}
                  onChange={(e) => setCep(maskCep(e.target.value))}
                  onBlur={handleCepBlur}
                  maxLength={9}
                  placeholder="00000-000"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                />
                {cepLoading && (
                  <div className="absolute right-2 top-2.5 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Rua</label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                maxLength={200}
                placeholder="Rua / Avenida"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Número</label>
              <input
                type="text"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                maxLength={20}
                placeholder="123"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Complemento</label>
              <input
                type="text"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
                maxLength={100}
                placeholder="Sala 2"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Bairro</label>
              <input
                type="text"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
                maxLength={100}
                placeholder="Centro"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Cidade</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={100}
                placeholder="São Paulo"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Estado</label>
              <input
                type="text"
                value={addrState}
                onChange={(e) => setAddrState(e.target.value.toUpperCase())}
                maxLength={2}
                placeholder="SP"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="rounded-lg border border-input p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={listedInSearch}
                onChange={(e) => setListedInSearch(e.target.checked)}
                className="mt-0.5 rounded border-input"
              />
              <span>
                <span className="block text-sm font-medium">
                  Exibir minha oficina na busca e no mapa
                </span>
                <span className="block text-xs text-muted-foreground">
                  {listedInSearch
                    ? 'Clientes que procuram oficina na região encontram a sua no app MustAuto.'
                    : 'Sua oficina não aparece na busca nem no mapa. Quem já tem OS com você continua vendo o perfil pelo link.'}
                </span>
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-muted-foreground">
              Especialidades
            </label>
            <SpecialtyPicker value={specialties} onToggle={toggleSpecialty} />
          </div>

          {/* Limiar de "OS parada". Fica colado nas especialidades porque é
              delas que sai a sugestão — o gestor vê a causa e o efeito juntos. */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-muted-foreground">
              Avisar quando uma OS ficar parada
            </label>
            <p className="text-xs text-muted-foreground">
              Tempo sem movimento até a OS entrar nos alertas do dia. Oficina de
              funilaria trabalha em dias, não em horas — por isso a sugestão muda
              conforme as especialidades acima. Quando a OS é aberta com um tipo
              de serviço (mecânica, funilaria, estética), vale o tempo do tipo;
              este vale para as OS sem tipo.
            </p>
            {/* Escolha única, e por isso NÃO se parece com os chips de
                especialidade logo acima: lá o gestor marca vários, aqui só um.
                Dois controles com semântica diferente e aparência igual é como
                se aprende a errar. */}
            <div
              role="radiogroup"
              aria-label="Tempo sem movimento até a OS entrar nos alertas"
              className="flex flex-wrap gap-2"
            >
              {STALE_OS_HOURS_CHOICES.map((option) => {
                const active = option === staleOsHours;
                const suggested = option === suggestedStaleOsHours;
                return (
                  <label
                    key={option}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                      'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1',
                      active
                        ? 'border-primary bg-primary/10 font-medium text-foreground'
                        : 'border-input text-muted-foreground hover:border-primary/50',
                    )}
                  >
                    <input
                      type="radio"
                      name="staleOsHours"
                      value={option}
                      checked={active}
                      onChange={() => setStaleOsHours(option)}
                      className="border-input"
                    />
                    <span>{staleOsHoursLabel(option)}</span>
                    {suggested && (
                      <span className="text-xs text-muted-foreground">sugerido</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tipos de serviço — logo abaixo do alerta de OS parada, porque é esta
          seção que explica que aquele tempo agora vale só para OS sem tipo. */}
      {/* O papel NA OFICINA atual (`workshop_members`), que é o que a API
          confere — o `role` do usuário pode ser outro numa conta multi-oficina. */}
      <WorkshopModulesCard
        readOnly={isDemo || (user?.workshops?.find((w) => w.isCurrent)?.role ?? user?.role) !== 'manager'}
      />

      {/* Etapas (F3.8): nome, ordem, foto, tempo (F3.7) e escondidas — logo
          depois dos tipos, porque só existe para os tipos com etapas ligados. */}
      <StagesCard
        readOnly={isDemo || (user?.workshops?.find((w) => w.isCurrent)?.role ?? user?.role) !== 'manager'}
      />

      {/* Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Horários de funcionamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {hours.map((h) => (
              <div key={h.day} className="flex items-center gap-3">
                <span className="w-20 text-sm font-medium">{DAY_NAMES[h.day]}</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={h.closed}
                    onChange={(e) => updateHour(h.day, 'closed', e.target.checked)}
                    className="rounded border-input"
                  />
                  <span className="text-xs text-muted-foreground">Fechado</span>
                </label>
                {!h.closed && (
                  <>
                    <input
                      type="time"
                      value={h.open}
                      onChange={(e) => updateHour(h.day, 'open', e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">até</span>
                    <input
                      type="time"
                      value={h.close}
                      onChange={(e) => updateHour(h.day, 'close', e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Photos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fotos da oficina (max 5)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {photos.map((key, i) => (
              <div key={key} className="relative h-24 w-24 rounded-lg border overflow-hidden group">
                <img
                  src={photoSrc(key)}
                  alt={`Foto ${i + 1}`}
                  className="h-full w-full object-cover"
                />
                <button
                  onClick={() => handleRemovePhoto(i)}
                  aria-label="Remover foto"
                  className="absolute top-1 right-1 rounded-full bg-destructive p-0.5 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {photos.length < 5 && (
              <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary/50 transition-colors">
                {uploading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                ) : (
                  <>
                    <Upload size={20} />
                    <span className="mt-1 text-[10px]">Upload</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dados de demonstração — só a saída, e só enquanto houver o que limpar. */}
      {hasDemoData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dados de demonstração</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-950/40">
                <Database size={20} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Esta oficina tem OS, veículos e estoque fictícios, carregados
                  para demonstração. Limpe quando quiser começar a usar para
                  valer — seus dados reais não são afetados.
                </p>
                <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle size={12} />
                  A limpeza é permanente
                </p>
                <button
                  onClick={() => { void handleClearDemoData(); }}
                  disabled={clearDemoData.isPending}
                  className="mt-3 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/40"
                >
                  {clearDemoData.isPending ? 'Limpando...' : 'Limpar dados de demonstração'}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Salvando...' : 'Salvar perfil'}
        </button>
      </div>
    </div>
  );
}

// ========================================
// Aba — Prestadores
// ========================================

// Chaves e rótulos vêm de @mustauto/types (fonte única, compartilhada com a
// API, o web-partner e o app). A cor é específica do Tailwind e fica aqui.
const CATEGORY_COLORS: Record<string, string> = {
  engine_rebuild: SOFT_BADGE_CLASSES.red,
  '3d_printing': SOFT_BADGE_CLASSES.purple,
  bodywork: SOFT_BADGE_CLASSES.amber,
  advanced_electrical: SOFT_BADGE_CLASSES.blue,
  adaptation: SOFT_BADGE_CLASSES.emerald,
  other: SOFT_BADGE_CLASSES.gray,
};

const PROVIDER_CATEGORIES = SERVICE_CATEGORIES.map((c) => ({
  value: c.key,
  label: c.label,
  color: CATEGORY_COLORS[c.key] ?? SOFT_BADGE_CLASSES.gray,
}));

function categoryLabel(value: string) {
  return getServiceCategoryLabel(value);
}

function categoryColor(value: string) {
  return CATEGORY_COLORS[value] ?? SOFT_BADGE_CLASSES.gray;
}

function ProvidersTab() {
  const {
    providers,
    isLoading,
    create,
    isCreating,
    update,
    deactivate,
  } = useServiceProviders();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

  const activeProviders = providers.filter((p) => p.active);
  const categoriesCovered = new Set(activeProviders.map((p) => p.category)).size;

  const filtered = categoryFilter.length > 0
    ? activeProviders.filter((p) => categoryFilter.includes(p.category))
    : activeProviders;

  const toggleCategoryFilter = (cat: string) => {
    setCategoryFilter((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="text-2xl font-bold">{activeProviders.length}</div>
            <div className="text-xs text-muted-foreground">Prestadores ativos</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-2xl font-bold">{categoriesCovered}</div>
            <div className="text-xs text-muted-foreground">Categorias cobertas</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {PROVIDER_CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => toggleCategoryFilter(cat.value)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
              categoryFilter.includes(cat.value)
                ? 'border-primary bg-accent text-primary'
                : 'border-border hover:bg-muted'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Add button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          {showAddForm ? 'Cancelar' : 'Novo prestador'}
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <ProviderForm
          onSubmit={async (data) => {
            try {
              await create(data);
              setShowAddForm(false);
              toast({ type: 'success', title: 'Prestador cadastrado' });
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao cadastrar prestador',
                description: formatApiError(err, 'Verifique os campos e tente novamente.'),
              });
            }
          }}
          isSubmitting={isCreating}
        />
      )}

      {/* Edit modal */}
      {editingProvider && (
        <ProviderEditModal
          provider={editingProvider}
          onSubmit={async (data) => {
            try {
              await update({ id: editingProvider.id, ...data });
              setEditingProvider(null);
              toast({ type: 'success', title: 'Prestador atualizado' });
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao atualizar prestador',
                description: formatApiError(err, 'Verifique os campos e tente novamente.'),
              });
            }
          }}
          onDeactivate={async () => {
            try {
              await deactivate(editingProvider.id);
              setEditingProvider(null);
              toast({ type: 'success', title: 'Prestador desativado' });
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao desativar',
                description: formatApiError(err, 'Tente novamente em instantes.'),
              });
            }
          }}
          onCancel={() => setEditingProvider(null)}
        />
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Wrench size={32} />}
          title={categoryFilter.length > 0 ? 'Nenhum prestador nas categorias filtradas' : 'Nenhum prestador cadastrado'}
          description={
            categoryFilter.length > 0
              ? 'Tente desmarcar algumas categorias ou cadastre um prestador novo.'
              : 'Cadastre oficinas parceiras pra terceirização de serviços específicos (alinhamento, suspensão, retífica).'
          }
          action={{
            label: 'Cadastrar prestador',
            onClick: () => setShowAddForm(true),
          }}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead className="text-center">Avaliação</TableHead>
                <TableHead className="text-right">OS realizadas</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>
                    <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${categoryColor(p.category)}`}>
                      {categoryLabel(p.category)}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {p.phone ? (
                      <span className="flex items-center gap-1">
                        <Phone size={12} />
                        {maskPhone(p.phone)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {p.internalRating ? (
                      <span className="text-amber-500 dark:text-amber-400">
                        {'★'.repeat(p.internalRating)}{'☆'.repeat(5 - p.internalRating)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{p.osCount}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingProvider(p)}
                        title="Editar"
                        aria-label="Editar prestador"
                        className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Desativar este prestador?',
                            description: 'O prestador some das listas mas não é apagado — pode reativar depois.',
                            confirmLabel: 'Desativar',
                            dangerous: true,
                          });
                          if (ok) deactivate(p.id);
                        }}
                        title="Desativar"
                        aria-label="Desativar prestador"
                        className="rounded-md border border-border p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function ProviderForm({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (data: CreateProviderData) => Promise<void>;
  isSubmitting: boolean;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [cep, setCep] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');

  const handleCepBlur = useCallback(async () => {
    if (!isValidCep(cep)) return;
    const found = await lookupCep(cep);
    if (!found) return;
    setStreet(found.street);
    setNeighborhood(found.neighborhood);
    setCity(found.city);
    setAddrState(found.state);
  }, [cep]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await onSubmit({
        name,
        category,
        phone: phone || undefined,
        email: email || undefined,
        addressCep: cep || undefined,
        addressStreet: street || undefined,
        addressNumber: number || undefined,
        addressComplement: complement || undefined,
        addressNeighborhood: neighborhood || undefined,
        addressCity: city || undefined,
        addressState: addrState || undefined,
        internalRating: rating > 0 ? rating : undefined,
        notes: notes || undefined,
      });
    },
    [name, category, phone, email, cep, street, number, complement, neighborhood, city, addrState, rating, notes, onSubmit],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold text-foreground">Novo prestador</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
                placeholder="Ex: Retifica Zona Sul"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Categoria *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Selecione</option>
                {PROVIDER_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Telefone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(maskPhone(e.target.value))}
                maxLength={15}
                placeholder="(11) 99999-9999"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={200}
                placeholder="contato@prestador.com"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">CEP</label>
              <input
                type="text"
                value={cep}
                onChange={(e) => setCep(maskCep(e.target.value))}
                onBlur={handleCepBlur}
                maxLength={9}
                placeholder="00000-000"
                className="rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              />
            </div>
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Rua</label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                maxLength={200}
                placeholder="Rua / Avenida"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Número</label>
              <input
                type="text"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                maxLength={20}
                placeholder="123"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Complemento</label>
              <input
                type="text"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
                maxLength={100}
                placeholder="Sala 2"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Bairro</label>
              <input
                type="text"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
                maxLength={100}
                placeholder="Centro"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Cidade</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={100}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Estado</label>
              <input
                type="text"
                value={addrState}
                onChange={(e) => setAddrState(e.target.value.toUpperCase())}
                maxLength={2}
                placeholder="SP"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Avaliação interna</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setRating(rating === v ? 0 : v)}
                  className={`text-xl ${v <= rating ? 'text-amber-400 dark:text-amber-300' : 'text-muted-foreground/40'}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Observação</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Notas internas sobre este prestador"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting || !name.trim() || !category}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Salvando...' : 'Adicionar prestador'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
}

function ProviderEditModal({
  provider,
  onSubmit,
  onDeactivate,
  onCancel,
}: {
  provider: ServiceProvider;
  onSubmit: (data: UpdateProviderData) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onCancel: () => void;
}) {
  useEscapeKey(onCancel);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [name, setName] = useState(provider.name);
  const [category, setCategory] = useState(provider.category);
  const [phone, setPhone] = useState(maskPhone(provider.phone ?? ''));
  const [email, setEmail] = useState(provider.email ?? '');
  const [cep, setCep] = useState(provider.addressCep ?? '');
  const [street, setStreet] = useState(provider.addressStreet ?? '');
  const [number, setNumber] = useState(provider.addressNumber ?? '');
  const [complement, setComplement] = useState(provider.addressComplement ?? '');
  const [neighborhood, setNeighborhood] = useState(provider.addressNeighborhood ?? '');
  const [city, setCity] = useState(provider.addressCity ?? '');
  const [addrState, setAddrState] = useState(provider.addressState ?? '');
  const [rating, setRating] = useState(provider.internalRating ?? 0);
  const [notes, setNotes] = useState(provider.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleCepBlur = useCallback(async () => {
    if (!isValidCep(cep)) return;
    const found = await lookupCep(cep);
    if (!found) return;
    setStreet(found.street);
    setNeighborhood(found.neighborhood);
    setCity(found.city);
    setAddrState(found.state);
  }, [cep]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSaving(true);
      try {
        await onSubmit({
          name,
          category,
          phone: phone || null,
          email: email || null,
          addressCep: cep || null,
          addressStreet: street || null,
          addressNumber: number || null,
          addressComplement: complement || null,
          addressNeighborhood: neighborhood || null,
          addressCity: city || null,
          addressState: addrState || null,
          internalRating: rating > 0 ? rating : null,
          notes: notes || null,
        });
      } finally {
        setSaving(false);
      }
    },
    [name, category, phone, email, cep, street, number, complement, neighborhood, city, addrState, rating, notes, onSubmit],
  );

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            Editar prestador
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Nome</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={200}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Categoria</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {PROVIDER_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Telefone</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                  maxLength={16}
                  placeholder="(11) 99999-9999"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={200}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">CEP</label>
                <input
                  type="text"
                  value={cep}
                  onChange={(e) => setCep(e.target.value)}
                  onBlur={handleCepBlur}
                  maxLength={9}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Rua</label>
                <input
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  maxLength={200}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Número</label>
                <input
                  type="text"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  maxLength={20}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Complemento</label>
                <input
                  type="text"
                  value={complement}
                  onChange={(e) => setComplement(e.target.value)}
                  maxLength={100}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Bairro</label>
                <input
                  type="text"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  maxLength={100}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Cidade</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={100}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Estado</label>
                <input
                  type="text"
                  value={addrState}
                  onChange={(e) => setAddrState(e.target.value.toUpperCase())}
                  maxLength={2}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Avaliação</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRating(rating === v ? 0 : v)}
                    className={`text-xl ${v <= rating ? 'text-amber-400 dark:text-amber-300' : 'text-muted-foreground/40'}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Observação</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                rows={2}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={onDeactivate}
                className="ml-auto rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/40 transition-colors"
              >
                Desativar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ========================================
// Aba 4 — Notificações
// ========================================

function NotificationsTab() {
  const { email, weeklyDigest, isLoading, update, isSaving } = useEmailSettings();
  const [emailValue, setEmailValue] = useState(email ?? '');
  const [digestValue, setDigestValue] = useState(weeklyDigest);
  const [initialized, setInitialized] = useState(false);

  if (!isLoading && !initialized) {
    setEmailValue(email ?? '');
    setDigestValue(weeklyDigest);
    setInitialized(true);
  }

  const handleSave = useCallback(async () => {
    await update({
      email: emailValue.trim() || null,
      weeklyDigest: digestValue,
    });
  }, [emailValue, digestValue, update]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const emailEmpty = !emailValue.trim();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Email e Resumo Semanal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Email</label>
            <input
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              maxLength={200}
              placeholder="seu@email.com"
              className="max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="weekly-digest"
              checked={digestValue}
              onChange={(e) => setDigestValue(e.target.checked)}
              disabled={emailEmpty}
              className="mt-0.5 rounded border-input"
            />
            <div>
              <label htmlFor="weekly-digest" className="text-sm font-medium cursor-pointer">
                Ativar resumo semanal
              </label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {emailEmpty
                  ? 'Informe seu email primeiro para ativar o resumo semanal.'
                  : 'Receba toda segunda-feira um resumo com as métricas da semana anterior.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
